import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, ChevronDown, Phone } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Button } from './ui/button';

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const handleHashLink = (e, path) => {
    const hashIndex = path.indexOf('#');
    if (hashIndex === -1) return; // no hash, let Link handle it
    
    const targetPath = path.substring(0, hashIndex);
    const hash = path.substring(hashIndex + 1);
    
    if (location.pathname === targetPath) {
      // Already on the page — scroll directly
      e.preventDefault();
      setIsMobileMenuOpen(false);
      const element = document.getElementById(hash);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } else {
      // Navigating to a different page — close menu immediately
      setIsMobileMenuOpen(false);
    }
    // ScrollToTop in App.js handles hash scroll on page load
  };

  const services = [
    { name: 'Airport Transfers', path: '/services#airport-transfers' },
    { name: 'Hourly Chauffeur Service', path: '/services#hourly-service' },
    { name: 'City to City Transportation', path: '/services#city-to-city' },
    { name: 'Event Transportation', path: '/services#event-transportation' },
  ];

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About Us', path: '/about' },
    { name: 'Book a Ride', path: '/book' },
    { name: 'Contact Us', path: '/contact' },
    { name: 'My Bookings', path: '/portal' },
  ];

  return (
    <>
      <header
        data-testid="main-header"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled
            ? 'bg-[#050505]/95 backdrop-blur-md border-b border-white/5'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between h-20 lg:h-24">
            {/* Logo */}
            <Link 
              to="/" 
              data-testid="logo-link"
              className="flex items-center gap-3"
            >
              <div className="flex flex-col">
                <span className="font-serif text-lg lg:text-xl text-white tracking-wide">
                  Capital
                </span>
                <span className="text-[10px] lg:text-xs text-[#D4AF37] tracking-[0.2em] uppercase -mt-1">
                  Chauffeur Services
                </span>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-8">
              {navLinks.slice(0, 2).map((link) => (
                <Link
                  key={link.name}
                  to={link.path}
                  data-testid={`nav-${link.name.toLowerCase().replace(/\s/g, '-')}`}
                  className={`nav-link text-sm ${
                    location.pathname === link.path ? 'text-[#D4AF37]' : 'text-white'
                  }`}
                >
                  {link.name}
                </Link>
              ))}

              {/* Services Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger 
                  data-testid="services-dropdown-trigger"
                  className="flex items-center gap-1 nav-link text-sm text-white outline-none"
                >
                  Services
                  <ChevronDown className="w-4 h-4" />
                </DropdownMenuTrigger>
                <DropdownMenuContent 
                  className="bg-[#0A0A0A] border border-white/10 backdrop-blur-xl min-w-[240px]"
                >
                  {services.map((service) => (
                    <DropdownMenuItem key={service.name} asChild>
                      <Link
                        to={service.path}
                        onClick={(e) => handleHashLink(e, service.path)}
                        data-testid={`dropdown-${service.name.toLowerCase().replace(/\s/g, '-')}`}
                        className="text-white hover:text-[#D4AF37] hover:bg-white/5 cursor-pointer px-4 py-3"
                      >
                        {service.name}
                      </Link>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              {navLinks.slice(2).map((link) => (
                <Link
                  key={link.name}
                  to={link.path}
                  data-testid={`nav-${link.name.toLowerCase().replace(/\s/g, '-')}`}
                  className={`nav-link text-sm ${
                    location.pathname === link.path ? 'text-[#D4AF37]' : 'text-white'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
            </nav>

            {/* CTA Button & Mobile Menu Toggle */}
            <div className="flex items-center gap-4">
              <a 
                href="tel:+12026840534"
                data-testid="header-phone"
              >
                <Button
                  className="hidden md:inline-flex bg-white/10 border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black rounded-none px-6 py-5 text-xs font-medium tracking-wider uppercase items-center gap-2"
                >
                  <Phone className="w-4 h-4" />
                  (202) 684-0534
                </Button>
              </a>
              
              <Link to="/book">
                <Button
                  data-testid="header-book-now-btn"
                  className="hidden sm:flex bg-[#D4AF37] text-black hover:bg-[#B5952F] rounded-none px-6 py-5 text-xs font-medium tracking-wider uppercase"
                >
                  Book Now
                </Button>
              </Link>

              {/* Mobile Menu Button */}
              <button
                data-testid="mobile-menu-toggle"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="lg:hidden p-2 text-white hover:text-[#D4AF37] transition-colors"
              >
                {isMobileMenuOpen ? (
                  <X className="w-6 h-6" />
                ) : (
                  <Menu className="w-6 h-6" />
                )}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div 
          data-testid="mobile-menu"
          className="fixed inset-0 bg-[#050505] z-40 pt-24 px-6 lg:hidden overflow-y-auto"
        >
          <nav className="flex flex-col gap-6">
            {navLinks.slice(0, 2).map((link) => (
              <Link
                key={link.name}
                to={link.path}
                className={`text-2xl font-serif ${
                  location.pathname === link.path ? 'text-[#D4AF37]' : 'text-white'
                }`}
              >
                {link.name}
              </Link>
            ))}

            {/* Mobile Services Section */}
            <div className="flex flex-col gap-4">
              <span className="text-2xl font-serif text-white">Services</span>
              <div className="flex flex-col gap-3 pl-4 border-l border-[#D4AF37]/30">
                {services.map((service) => (
                  <Link
                    key={service.name}
                    to={service.path}
                    onClick={(e) => handleHashLink(e, service.path)}
                    className="text-[#A3A3A3] hover:text-[#D4AF37] transition-colors"
                  >
                    {service.name}
                  </Link>
                ))}
              </div>
            </div>

            {navLinks.slice(2).map((link) => (
              <Link
                key={link.name}
                to={link.path}
                className={`text-2xl font-serif ${
                  location.pathname === link.path ? 'text-[#D4AF37]' : 'text-white'
                }`}
              >
                {link.name}
              </Link>
            ))}

            {/* Mobile CTA */}
            <div className="pt-8 border-t border-white/10 mt-4">
              <a 
                href="tel:+12026840534"
                className="flex items-center gap-3 text-white mb-6"
              >
                <Phone className="w-5 h-5 text-[#D4AF37]" />
                <span>+1 (202) 684-0534</span>
              </a>
              <Link to="/book" className="block">
                <Button className="w-full bg-[#D4AF37] text-black hover:bg-[#B5952F] rounded-none py-4 text-sm font-medium tracking-wider uppercase">
                  Book Now
                </Button>
              </Link>
            </div>
          </nav>
        </div>
      )}
    </>
  );
};
